/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect, useRef } from 'react';
import { useTriggerIssueCreateModal } from '@jira/issue-create__use-trigger-issue-create-modal/src/main.tsx';
import { isSupportedUserAgent } from '@jira/platform__is-mobile-user-agent/src/index.tsx';
import { SERVICE_DESK_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
// eslint-disable-next-line jira/restricted/@jira+platform__services-providers-project-context
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { usePersistentIssueCreateModalViewResource } from '@jira/router-resources__persistent-issue-create-modal-view/src/usePersistentIssueCreateModalViewResource.tsx';

export const useInitializePersistentIssueCreateModalView = (): null => {
	const [
		{ shouldShowMiniModalSpotlight },
		{ setShouldPushMiniModalDiscoverability, setPersistentModalView },
	] = useTriggerIssueCreateModal();
	const { data: projectContext } = useProjectContext();
	const hasUserSeenMiniModal = useRef(false);
	const isJSM = projectContext?.projectType === SERVICE_DESK_PROJECT;
	const { data: resource } = usePersistentIssueCreateModalViewResource();

	/**
	 * It only handles the initialization part as after the modal is opened the persistentModalView
	 * would be handled by the `actions` of `useTriggerIssueCreateModal`.
	 */
	useEffect(() => {
		setPersistentModalView(resource?.view || 'modal');

		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * JSM has opted out so we update the `shouldPushMiniModalDiscoverability` on every project change
	 * until the discoverability push has been done, meaning that the user has seen the mini modal being
	 * shown as the default modal.
	 *
	 * Mobile is also opting out of having any mini modal discoverability and not interacting with any of the discoverability state
	 * for if the user returns to desktop.
	 *
	 * If the user has seen the mini modal from discoverability push (hasUserSeenMiniModal) or
	 * dismiss its nudge (isDiscoPushComplete), shouldPushMiniModalDiscoverability should never be true again.
	 */
	useEffect(() => {
		if (resource?.isDiscoPushComplete || hasUserSeenMiniModal.current) {
			setShouldPushMiniModalDiscoverability(false);
		} else {
			setShouldPushMiniModalDiscoverability(!isJSM && !isSupportedUserAgent());
		}
	}, [resource?.isDiscoPushComplete, setShouldPushMiniModalDiscoverability, isJSM]);

	/**
	 * `shouldShowMiniModalSpotlight` can be used to determine if user has seen the mini modal
	 * as it is only set `onModalOpen`, which means, if it is true, the modal is opened with mini view
	 * and the user will see it.
	 */
	useEffect(() => {
		if (shouldShowMiniModalSpotlight && !hasUserSeenMiniModal.current) {
			hasUserSeenMiniModal.current = true;
		}
	}, [shouldShowMiniModalSpotlight]);

	return null;
};
